import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../utils/hooks/use-auth';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { Spacer } from '../../../utils/components/Spacer';
import { StyledButton } from '../../styles/kanriStyle';
import { CustomerManagementRecordInterface, CustomerManagementJsonToCsvInterface } from '../../interfaces/CustomerManagement';

type Props = {
  setSearchConditionUserId: (userId?: string) => void;
  setSearchConditionContractId: (contractId?: string) => void;
  setSearchConditionContractName: (contractName?: string) => void;
  setSearchConditionRequestPointId: (requestPointId?: string) => void;
  setSearchConditionRequestPointName: (requestPointName?: string) => void;
  setSearchConditionSupplyDate: (startHead: Dayjs|null, startTail: Dayjs|null, EndHead: Dayjs|null, EndTail: Dayjs|null) => void;
  setSearchConditionUnit: (contractUnit?: boolean, requestPointUnit?: boolean) => void;
  setSearchConditionMailAddress: (mailAddressRegister?: boolean, noMailAddressRegister?: boolean) => void;
  setSearchConditionIdStatus: (idStatNotified?: boolean, idStatUnnotified?: boolean) => void;
  setSearchConditionSendMail: (mailNecessary?: boolean, mailUnnecessary?: boolean) => void;
  searchedData: CustomerManagementRecordInterface[];
  searchData: () => void;
  resetData: () => void;
}

interface Field {
  label: string;
  value: keyof CustomerManagementJsonToCsvInterface;
}

const fields: Field[] = [
  { label: '請求先ID', value: 'contract_id' },
  { label: '契約名', value: 'contract_name' },
  { label: '需要場所ID', value: 'display_request_point_id' },
  { label: '需要場所名', value: 'display_request_point_name' },
  { label: '供給地点特定番号', value: 'supply_point_no' },
  { label: '供給開始日', value: 'supply_start_date' },
  { label: '供給終了日', value: 'contract_end_date' },
  { label: '種別(料金プラン)', value: 'fee_menu_name_printing' },
  { label: '契約容量', value: 'contract_power_usually' },
  { label: 'ユーザID', value: 'user_id' },
  { label: 'アカウント種別', value: 'user_type' },
  { label: 'メールアドレス', value: 'mail_address' },
  { label: 'ID通知フラグ', value: 'id_notice_status' },
  { label: 'メール不要フラグ', value: 'mail_send_necessity' },
];

export default function SearchBoxCustomerManagement(props: Props) {
  const auth = useAuth();
  const [userId, setUserId] = useState<string>('');
  const [contractId, setContractId] = useState<string>('');
  const [contractName, setContractName] = useState<string>('');
  const [requestPointId, setRequestPointId] = useState<string>('');
  const [requestPointName, setRequestPointName] = useState<string>('');
  const [startDateHead, setStartDateHead] = useState<Dayjs | null>(null);
  const [startDateTail, setStartDateTail] = useState<Dayjs | null>(null);
  const [endDateHead, setEndDateHead] = useState<Dayjs | null>(dayjs());
  const [endDateTail, setEndDateTail] = useState<Dayjs | null>(null);
  const [checkedContractUnit, setCheckedContractUnit] = useState(true);
  const [checkedRequestPointUnit, setCheckedRequestPointUnit] = useState(true);
  const [checkedMailAddressRegister, setCheckedMailAddressRegister] = useState(true);
  const [checkedNoMailAddressRegister, setCheckedNoMailAddressRegister] = useState(true);
  const [checkedIdStatNotified, setCheckedIdStatNotified] = useState(true);
  const [checkedIdStatUnnotified, setCheckedIdStatUnnotified] = useState(true);
  const [checkedMailNecessary, setCheckedMailNecessary] = useState(true);
  const [checkedMailUnnecessary, setCheckedMailUnnecessary] = useState(true);
  
  const {
    setSearchConditionUserId,
    setSearchConditionContractId,
    setSearchConditionContractName,
    setSearchConditionRequestPointId,
    setSearchConditionRequestPointName,
    setSearchConditionSupplyDate,
    setSearchConditionUnit,
    setSearchConditionMailAddress,
    setSearchConditionIdStatus,
    setSearchConditionSendMail,
    searchedData,
    searchData,
    resetData
  } = props;
  
  const handleChangeSupplyStartHead = (newValue: Dayjs| null) => {
     setStartDateHead(newValue);
     setSearchConditionSupplyDate(newValue, null, null, null);
  };
  const handleChangeSupplyStartTail = (newValue: Dayjs | null) => {
     setStartDateTail(newValue);
     setSearchConditionSupplyDate(null, newValue, null, null);
  };
  const handleChangeSupplyEndHead = (newValue: Dayjs | null) => {
     setEndDateHead(newValue);
     setSearchConditionSupplyDate(null, null, newValue, null);
  };
  const handleChangeSupplyEndTail = (newValue: Dayjs | null) => {
     setEndDateTail(newValue);
     setSearchConditionSupplyDate(null, null, null, newValue);
  };
  
  useEffect(() => {
    setSearchConditionUnit(checkedContractUnit, checkedRequestPointUnit);
  }, [checkedContractUnit, checkedRequestPointUnit])
  
  useEffect(() => {
    setSearchConditionMailAddress(checkedMailAddressRegister, checkedNoMailAddressRegister);
  }, [checkedMailAddressRegister, checkedNoMailAddressRegister])
  
  useEffect(() => {
    setSearchConditionIdStatus(checkedIdStatNotified, checkedIdStatUnnotified);
  }, [checkedIdStatNotified, checkedIdStatUnnotified])
  
  useEffect(() => {
    setSearchConditionSendMail(checkedMailNecessary, checkedMailUnnecessary);
  }, [checkedMailNecessary, checkedMailUnnecessary])
  
  const onClickClearButton = () => {
    setUserId("");
    setContractId("");
    setContractName("");
    setRequestPointId("");
    setRequestPointName("");
    setStartDateHead(null);
    setStartDateTail(null);
    setEndDateHead(dayjs());
    setEndDateTail(null);
    setCheckedContractUnit(true);
    setCheckedRequestPointUnit(true);
    setCheckedMailAddressRegister(true);
    setCheckedNoMailAddressRegister(true);
    setCheckedIdStatNotified(true);
    setCheckedIdStatUnnotified(true);
    setCheckedMailNecessary(true);
    setCheckedMailUnnecessary(true);
    resetData();
  }
  
  const convertJsonToCsv = (data: CustomerManagementJsonToCsvInterface[]) => {
    const csvRows = [];
    const headers = fields.map(field => field.label);
    csvRows.push(headers.join(','));

    for (const row of data) {
      if (row.user_type_code === "01") {
        row.user_type = '契約単位';
      } else {
        row.user_type = '需要場所単位';
      }
      
      if (row.id_notice_status_code === "1") {
        row.id_notice_status = "済"
      } else  {
        row.id_notice_status = "未"
      }
      
      if (row.mail_send_necessity_code === "1") {
        row.mail_send_necessity = "必要"
      } else {
        row.mail_send_necessity = "不要"
      }
      
      const values = fields.map(field => {
        const escaped = ('' + row[field.value]).replace(/"/g, '\\"');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    }
    return csvRows.join('\r\n');
  };
  
  const BOM = "\uFEFF";
  const csv = BOM + convertJsonToCsv(searchedData);

  const downloadCsv = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const hour = ('0' + date.getHours()).slice(-2);
    const minute = ('0' + date.getMinutes()).slice(-2);
    const fileName = `${auth.username}_${year}${month}${day}${hour}${minute}.csv`;
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', href);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <div>
      <ListItemButton sx={{ bgcolor: '#172B65', color: '#FFFFFF', '&:hover': { backgroundColor: '#172B65' } }}>
        <ListItemText primary="検索条件" />
      </ListItemButton>
      <Paper sx={{ height: 400, padding: "0 40px" }} variant="outlined" square >
        <Spacer size={10} />
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={4}>
            <Typography variant="body1">ユーザID</Typography>
            <TextField 
              value={userId}
              size="small" 
              fullWidth
              onChange={(e) => {
                setUserId(e.target.value);
                setSearchConditionUserId(e.target.value)
              }}/>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body1">請求先ID</Typography>
            <TextField 
              value={contractId}
              size="small" 
              fullWidth
              onChange={(e) => {
                setContractId(e.target.value);
                setSearchConditionContractId(e.target.value)
              }}/>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body1">契約名</Typography>
            <TextField
              value={contractName}
              size="small"
              fullWidth
              onChange={(e) => {
                setContractName(e.target.value);
                setSearchConditionContractName(e.target.value)
              }}/>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body1">需要場所ID</Typography>
            <TextField
              value={requestPointId}
              size="small"
              fullWidth
              onChange={(e) => {
                setRequestPointId(e.target.value);
                setSearchConditionRequestPointId(e.target.value)
              }}/>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body1">需要場所名</Typography>
            <TextField
              value={requestPointName}
              size="small"
              fullWidth
              onChange={(e) => {
                setRequestPointName(e.target.value);
                setSearchConditionRequestPointName(e.target.value)
              }}/>
          </Grid>
          <Grid item xs={4}></Grid>
          <Grid item xs={4}>
            <Typography variant="body1">供給開始日</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='ja'>
                <DateTimePicker
                 value={startDateHead}
                 format="YYYY/MM/DD"
                 ampm={false}
                 slotProps={{ textField: { size: 'small', style: { backgroundColor: '#FFFFFF' }}}}
                 onChange={handleChangeSupplyStartHead}
                 views={['year', 'month', 'day']}
                />
              </LocalizationProvider>
              <Typography variant="h5">~</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='ja'>
                <DateTimePicker
                 value={startDateTail}
                 format="YYYY/MM/DD"
                 ampm={false}
                 slotProps={{ textField: { size: 'small', style: { backgroundColor: '#FFFFFF' }}}}
                 onChange={handleChangeSupplyStartTail}
                 views={['year', 'month', 'day']}
                />
              </LocalizationProvider>
            </Stack>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body1">供給終了日</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='ja'>
                <DateTimePicker
                 value={endDateHead}
                 format="YYYY/MM/DD"
                 ampm={false}
                 slotProps={{ textField: { size: 'small', style: { backgroundColor: '#FFFFFF' }}}}
                 onChange={handleChangeSupplyEndHead}
                 views={['year', 'month', 'day']}
                />
              </LocalizationProvider>
              <Typography variant="h5">~</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='ja'>
                <DateTimePicker
                  value={endDateTail}
                  format="YYYY/MM/DD"
                  ampm={false}
                  slotProps={{ textField: { size: 'small', style: { backgroundColor: '#FFFFFF' }}}}
                  onChange={handleChangeSupplyEndTail}
                  views={['year', 'month', 'day']}
                />
              </LocalizationProvider>
            </Stack>
          </Grid>
          <Grid item xs={4}></Grid>
          <Grid item xs={3}>
            <Typography variant="body1">ユーザ種別</Typography>
            <Stack direction="row" spacing={1}>
              <FormControlLabel control={<Checkbox checked={checkedContractUnit} onChange={(e) => setCheckedContractUnit(e.target.checked)} />} label="契約単位" />
              <FormControlLabel control={<Checkbox checked={checkedRequestPointUnit} onChange={(e) => setCheckedRequestPointUnit(e.target.checked)} />} label="需要場所単位" />
            </Stack>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body1">メールアドレス</Typography>
            <Stack direction="row" spacing={1}>
              <FormControlLabel control={<Checkbox checked={checkedMailAddressRegister} onChange={(e) => setCheckedMailAddressRegister(e.target.checked)} />} label="登録あり" />
              <FormControlLabel control={<Checkbox checked={checkedNoMailAddressRegister} onChange={(e) => setCheckedNoMailAddressRegister(e.target.checked)} />} label="登録なし" />
            </Stack>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body1">ID通知ステータス</Typography>
            <Stack direction="row" spacing={1}>
              <FormControlLabel control={<Checkbox checked={checkedIdStatUnnotified} onChange={(e) => setCheckedIdStatUnnotified(e.target.checked)} />} label="未通知" />
              <FormControlLabel control={<Checkbox checked={checkedIdStatNotified} onChange={(e) => setCheckedIdStatNotified(e.target.checked)} />} label="通知済み" />
            </Stack>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body1">メール送信要否</Typography>
            <Stack direction="row" spacing={1}>
              <FormControlLabel control={<Checkbox checked={checkedMailNecessary} onChange={(e) => setCheckedMailNecessary(e.target.checked)} />} label="要" />
              <FormControlLabel control={<Checkbox checked={checkedMailUnnecessary} onChange={(e) => setCheckedMailUnnecessary(e.target.checked)} />} label="不要" />
            </Stack>
          </Grid>
          <Grid item xs={9}></Grid>
          <Grid item xs={3}>
            <Box display="flex" justifyContent="flex-end">
              <StyledButton variant="outlined" onClick={searchData}>検索</StyledButton>
              <StyledButton variant="outlined" onClick={onClickClearButton}>クリア</StyledButton>
              <StyledButton variant="outlined" onClick={downloadCsv}>CSV出力</StyledButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
}